import { createClient } from "@connectrpc/connect";
import { ClienteFincaWizardService } from "../../../../../generated/grpc/terceros_finca_wizard_pb";
import { grpcTransportProvider } from "../../../../../comun/services/grpc/GrpcTransport";

/** El mapa del registro de finca habla SOLO con la Api (2026-10-11).
 *
 *  Antes la web le pedía todo directo a GeoServer: las imágenes (WMS), las
 *  geometrías (WFS) y las escrituras del trazo (WFS-T), con el usuario
 *  administrador de GeoServer escrito en el código y el aislamiento entre
 *  clientes puesto por el navegador (`CQL_FILTER=registro_id=…`). Ahora:
 *
 *   - las imágenes van a `/mapa/registro-finca/wms` de la Api, con un ticket
 *     firmado del registro (`ticketMapaRegistro`). La Api arma el filtro del
 *     registro; la web solo dice qué capa, qué estilo y datos sueltos validados
 *     (índice, pieza, lotes);
 *   - las lecturas y escrituras de geometría van por gRPC
 *     (`ClienteFincaWizardService`), que valida que el registro sea del cliente
 *     de la sesión.
 *
 *  GeoServer escucha solo dentro del servidor: la web no conoce ni su dirección
 *  ni sus credenciales. */

/** Claves de las capas que acepta la Api en `LAYERS`. Cada una es una capa
 *  publicada en GeoServer (`vw_poligonos_padre`, `vw_dibujo_en_curso`,
 *  `tbl_temp_vertices_dibujo`, `tbl_temp_lotes_finca`). */
export const MAPA_CAPA_POLIGONOS_PADRE = "poligonos-padre";
/** **Sin uso en el cliente desde el 2026-10-23**: el trazo en curso lo dibuja
 *  Leaflet, así que nadie pide ya esta capa. Se deja porque la capa sigue
 *  publicada en GeoServer y la Api la sigue aceptando. */
export const MAPA_CAPA_DIBUJO_EN_CURSO = "dibujo-en-curso";
/** Los vértices. Hoy sólo se piden los de UNA pieza guardada (modo Editar); el
 *  filtro `TRAZO=1`, que traía los del trazo en curso, dejó de usarse con el
 *  trazo en Leaflet. */
export const MAPA_CAPA_VERTICES = "vertices";
export const MAPA_CAPA_LOTES = "lotes";

/** URL de las imágenes del mapa del registro: la misma Api de gRPC-Web. */
export function fx_urlMapaRegistroFinca(): string {
  return `${grpcTransportProvider.baseRoute}/mapa/registro-finca/wms`;
}

/** Cliente gRPC del wizard, para las lecturas y escrituras del mapa. */
export function fx_clienteMapaRegistro() {
  return createClient(ClienteFincaWizardService, grpcTransportProvider.get());
}

/** Tickets de las imágenes del mapa, uno por registro.
 *
 *  **Por qué un ticket.** Leaflet pide cada imagen como un `<img>`, que no puede
 *  mandar el header `token` de la sesión. El ticket lo emite el servidor
 *  (`ObtenerTicketMapa`) después de comprobar que el registro es del cliente,
 *  vale unas horas y solo sirve para ese registro.
 *
 *  **Mientras no hay ticket no se pide ninguna imagen** (`SelectiveWms` no da
 *  por válido ningún tile): así no sale un pedido sin ticket que la Api
 *  rechazaría. Las capas repintan apenas llega (`asegurarAsync`).
 *
 *  Se renueva solo cuando faltan menos de `MARGEN_MS` para que venza. */
class TicketMapaRegistro {
  /** Se pide otro cuando falta menos que esto para el vencimiento. */
  private static readonly MARGEN_MS = 15 * 60 * 1000;

  /** Después de un fallo no se vuelve a pedir antes de esto: Leaflet pregunta
   *  por cada tile en cada movimiento del mapa, y sin pausa un error repetido
   *  se volvería una ráfaga de pedidos. */
  private static readonly PAUSA_TRAS_FALLO_MS = 10 * 1000;

  private readonly _tickets = new Map<string, { ticket: string; expiraEn: number }>();
  private readonly _pedidos = new Map<string, Promise<void>>();
  private readonly _ultimoFallo = new Map<string, number>();

  /** El ticket vigente del registro, o `""` si todavía no llegó. Si no hay o
   *  está por vencer, pide otro en segundo plano. */
  actual(registroId: string): string {
    const guardado = this._tickets.get(registroId);
    if (!guardado || this._porVencer(guardado.expiraEn)) {
      void this.asegurarAsync(registroId);
    }
    return guardado && Date.now() < guardado.expiraEn ? guardado.ticket : "";
  }

  /** Se asegura de tener un ticket vigente del registro. Un pedido en vuelo
   *  se comparte: varias capas montándose a la vez piden uno solo. */
  asegurarAsync(registroId: string): Promise<void> {
    const guardado = this._tickets.get(registroId);
    if (guardado && !this._porVencer(guardado.expiraEn)) return Promise.resolve();

    let pedido = this._pedidos.get(registroId);
    if (!pedido) {
      const fallo = this._ultimoFallo.get(registroId);
      if (fallo && Date.now() - fallo < TicketMapaRegistro.PAUSA_TRAS_FALLO_MS) {
        return Promise.resolve();
      }
      pedido = (async () => {
        try {
          const respuesta = await fx_clienteMapaRegistro().obtenerTicketMapa({
            registroId,
          });
          this._tickets.set(registroId, {
            ticket: respuesta.ticket,
            expiraEn: Number(respuesta.expiraEn),
          });
          this._ultimoFallo.delete(registroId);
        } catch (error) {
          // Sin ticket el mapa no muestra lo guardado, pero el resto del paso
          // sigue andando; se vuelve a intentar pasada la pausa.
          this._ultimoFallo.set(registroId, Date.now());
          console.error("No se pudo obtener el ticket del mapa del registro", error);
        } finally {
          this._pedidos.delete(registroId);
        }
      })();
      this._pedidos.set(registroId, pedido);
    }
    return pedido;
  }

  private _porVencer(expiraEn: number): boolean {
    return expiraEn - Date.now() < TicketMapaRegistro.MARGEN_MS;
  }
}

/** Singleton de los tickets del mapa del registro. */
export const ticketMapaRegistro = new TicketMapaRegistro();
